import { RedeemCreditKey } from '@/features/purchase/services/GiftCard/mutations'
import { type RedeemCreditResponse } from '@/features/purchase/services/GiftCard/types'
import { membershipPlanQueryOptions } from '@/features/account/services/MembershipPlan/queries'
import { epochSecondsToISO } from '@/features/account/services/MembershipPlan/utils'
import { getUser } from '@/features/account/services/User/utils'
import { getMutationCacheData } from '@/features/shared/utils/dataFetching/reactQuery/getMutationData'
import { getQueryClient } from '@/features/shared/utils/dataFetching/reactQuery/SegwayQueryClient'
import { formatDate } from '@/features/shared/utils/date'

export enum RedemptionModalType {
  PROMO_MEMBERSHIP = 'promo_membership',
  GIFT_MEMBERSHIP = 'gift_membership',
  GIFT_CARD_CREDIT = 'gift_card_credit',
  GIFT_CARD_MEMBERSHIP = 'gift_card_membership',
  MEMBERSHIP_PURCHASE = 'membership_purchase',
  MEMBERSHIP_EARLY_RENEWAL = 'membership_early_renewal',
  EBT_MEMBERSHIP = 'ebt_membership',
  STUDENT_MEMBERSHIP = 'student_membership',
}

export const getRedemptionModalContent = (type: string, endDate?: string) => {
  let title = 'Success!'
  let body =
    "We're glad you're here. Start saving with free delivery, exclusive benefits, and more."
  let secondaryActionCopy

  switch (type) {
    case RedemptionModalType.PROMO_MEMBERSHIP:
      title = "You've purchased a membership!"
      break
    case RedemptionModalType.GIFT_MEMBERSHIP:
      title = "You've redeemed a gift membership!"
      break
    case RedemptionModalType.GIFT_CARD_CREDIT:
      const creditResponse: RedeemCreditResponse = getMutationCacheData(RedeemCreditKey)
      const creditString = creditResponse.result.split('$')[1]
      const creditAmount = creditString?.split('.')[0]
      title = `You've added $${creditAmount} to your account!`
      body = "Time to get busy browsing - you've redeemed your Shipt gift card."
      break
    case RedemptionModalType.GIFT_CARD_MEMBERSHIP:
      title = "You've redeemed a membership!"
      break
    case RedemptionModalType.MEMBERSHIP_PURCHASE:
      title = "Congrats! You've got a Shipt Membership!"
      body =
        'Get shopping and start taking advantage of your exclusive benefits, personalized shopping, and more.'
      break
    case RedemptionModalType.MEMBERSHIP_EARLY_RENEWAL: {
      const queryClient = getQueryClient()
      const user = getUser(queryClient)
      const subscriptionResponse = queryClient.getQueryData(
        membershipPlanQueryOptions(user?.id ?? 0).queryKey,
      )
      const renewalDate =
        subscriptionResponse?.discount?.next_full_charge_at ??
        subscriptionResponse?.current_period_end
      const renewalDateObj = new Date(epochSecondsToISO(renewalDate))
      const newRenewalDate = formatDate(renewalDateObj, 'MMM d, yyyy')
      title = 'Your Shipt annual membership has been renewed!'
      body = `Your annual membership has been extended by a year. It will now auto renew on ${newRenewalDate}. Continue to access all your membership benefits, including no delivery fees, more deals and much more.`
      break
    }
    case RedemptionModalType.EBT_MEMBERSHIP: {
      title = 'Congrats on your Shipt membership!'
      body = `You're all set to access all your membership benefits, 
        including no delivery fees, more deals and much more. 
        Reminder: You may be asked periodically to re-validate your EBT card.`
      secondaryActionCopy = 'Explore membership benefits'
      break
    }
    case RedemptionModalType.STUDENT_MEMBERSHIP: {
      title = 'You’ve got a Shipt Membership!'
      body = `You’re all set to enjoy your exclusive benefits using your student membership until ${endDate}. We’ll notify you when it’s time to reverify your student status to continue your discounted rate.`
    }
  }

  return { title, body, secondaryActionCopy }
}
